import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Ticket as TicketIcon, MapPin, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { ticketsApi } from "@/lib/accountApi";
import { ticketQr } from "@/lib/totp";
import { Empty } from "@/components/partner/PartnerShell";
import { ticketPdf } from "@/lib/pdf";

const STATUS = { valid: ["Valid", "bg-emerald-100 text-emerald-800"], used: ["Used", "bg-muted text-foreground/70"], refunded: ["Refunded", "bg-rose-100 text-rose-800"], void: ["Void", "bg-rose-100 text-rose-800"] };

function when(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/** Live rotating QR (same TOTP as the app and the door scanner); the code text is the offline fallback. */
function LiveQr({ t }) {
  const [value, setValue] = useState("");
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!t.secret) return undefined;
    const offset = t.server_time ? t.server_time - Math.floor(Date.now() / 1000) : 0;
    let alive = true;
    const tick = async () => {
      const now = Math.floor(Date.now() / 1000) + offset;
      const v = await ticketQr(t.id, t.secret, now, t.step || 15);
      if (!alive) return;
      setValue(v);
      setLeft((t.step || 15) - (now % (t.step || 15)));
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [t.id, t.secret, t.server_time, t.step]);
  if (!t.secret) return null;
  return (
    <div className="flex flex-col items-center" data-testid="ticket-qr">
      <div className="rounded-2xl bg-white p-3">{value ? <QRCodeSVG value={value} size={220} level="M" /> : <Skeleton className="h-[220px] w-[220px]" />}</div>
      <p className="mt-2 text-xs text-muted-foreground">Refreshes in {left}s · works offline</p>
    </div>
  );
}

function TicketModal({ id, onClose }) {
  const [t, setT] = useState(undefined);
  useEffect(() => {
    ticketsApi.get(id).then(setT).catch((e) => { toast.error(e.message || "Couldn't open that ticket."); onClose(); });
  }, [id, onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6" onClick={onClose} role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-card p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="ticket-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t?.ticket_type_name || "Ticket"}</p>
            <h2 className="font-serif-display text-2xl font-bold">{t?.event?.title || "…"}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted" aria-label="Close"><X size={18} /></button>
        </div>
        {t === undefined ? (
          <Skeleton className="mt-6 h-64 rounded-2xl" />
        ) : (
          <>
            <div className="mt-4 space-y-1 text-sm text-muted-foreground">
              <p className="inline-flex items-center gap-1.5"><Clock size={14} /> {when(t.event.starts_at)}{t.event.doors_at ? ` · doors ${when(t.event.doors_at).split(", ").pop()}` : ""}</p>
              {(t.event.venue_name || t.event.address) && <p className="flex items-center gap-1.5"><MapPin size={14} /> {[t.event.venue_name, t.event.address].filter(Boolean).join(", ")}</p>}
              <p>Holder: <span className="font-medium text-foreground">{t.holder_name || "You"}</span></p>
            </div>
            <div className="mt-6">
              {t.usable && t.secret ? (
                <LiveQr t={t} />
              ) : (
                <p className="rounded-2xl bg-muted p-4 text-center text-sm">{t.status === "used" ? "This ticket has been scanned." : t.status === "refunded" ? "This ticket was refunded." : "This ticket is no longer valid."}</p>
              )}
              <p className="mt-4 text-center text-xs text-muted-foreground">Backup code at the door</p>
              <p className="text-center font-mono-num text-2xl font-bold tracking-widest" data-testid="ticket-code">{t.code}</p>
          <button type="button" className="mx-auto mt-2 block text-sm font-medium text-primary hover:underline" onClick={() => ticketPdf(t).catch(() => toast.error("Couldn't make the PDF"))} data-testid="ticket-pdf">Download PDF ticket</button>
              {t.event.online_url && <a href={t.event.online_url} target="_blank" rel="noopener noreferrer" className="mt-3 block text-center text-sm font-medium text-primary hover:underline">Join online</a>}
            </div>
            {t.cancel && (
              <p className="mt-5 text-xs text-muted-foreground">{t.cancel.message}{t.cancel.allowed ? " Cancel from the Fable + app." : ""}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Tickets() {
  usePageMeta("My tickets", "Your event tickets on Fable +, with the live QR for the door.");
  const [scope, setScope] = useState("upcoming");
  const [data, setData] = useState(undefined);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setData(undefined);
    ticketsApi.mine(scope).then((d) => setData(d.items || [])).catch(() => setData(null));
  }, [scope]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">My tickets</h1>
      <div className="mt-4 flex gap-2">
        {["upcoming", "past"].map((s) => (
          <button key={s} type="button" onClick={() => setScope(s)} aria-pressed={scope === s} data-testid={`tickets-${s}`} className={`rounded-full border px-4 py-1.5 text-sm font-medium capitalize ${scope === s ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{s}</button>
        ))}
      </div>
      <div className="mt-6" data-testid="tickets-list">
        {data === undefined ? (
          <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : data === null ? (
          <p className="py-10 text-center text-muted-foreground">Couldn't load your tickets.</p>
        ) : data.length === 0 ? (
          <Empty title={scope === "upcoming" ? "No upcoming tickets" : "No past tickets"} body="Find something good to go to — gigs, comedy, food markets and more." action={<Link to="/marketplace?world=experiences"><Button className="rounded-full">Browse experiences</Button></Link>} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {data.map((t) => {
              const [label, tone] = STATUS[t.status] || STATUS.valid;
              return (
                <button key={t.id} type="button" onClick={() => setOpen(t.id)} data-testid={`ticket-${t.id}`} className="overflow-hidden rounded-2xl border border-border bg-card text-left transition-colors hover:bg-muted/40">
                  {t.event?.cover_url && <img src={t.event.cover_url} alt="" className="h-32 w-full object-cover" />}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold">{t.event?.title}</h3>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{when(t.event?.starts_at)}</p>
                    <p className="text-sm text-muted-foreground">{[t.event?.venue_name, t.event?.city].filter(Boolean).join(" · ")}</p>
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium"><TicketIcon size={13} /> {t.ticket_type_name} · {t.holder_name || "You"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {open && <TicketModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
